import "./onboard.css";
import { onboardLabel } from "@railroute/contracts/rail";
import { useCallback, useEffect, useState } from "react";
import type {
  OnboardTrip,
  OnboardCatalog,
  ChecklistItem,
  OnboardDefect,
  OnboardRepair,
} from "@railroute/contracts/rail";
import { onboardWebRequest } from "../api";
export function OnboardSection({
  token,
  organizationId,
  organizationMembershipId,
  onOpenWorkOrder,
}: {
  token: string;
  organizationId: string;
  organizationMembershipId: string;
  onOpenWorkOrder?: (number: string) => void;
}) {
  const [trips, setTrips] = useState<OnboardTrip[]>([]),
    [catalog, setCatalog] = useState<OnboardCatalog | null>(null),
    [trip, setTrip] = useState<OnboardTrip | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [templateName, setTemplateName] = useState("Checklist БЭМ"),
    [templateId, setTemplateId] = useState(""),
    [items, setItems] = useState<ChecklistItem[]>([]);
  const api = useCallback(
    <T,>(path: string, body?: unknown) =>
      onboardWebRequest<T>(path, token, organizationId, body),
    [token, organizationId],
  );
  const load = useCallback(async () => {
    setTrips(await api<OnboardTrip[]>("/trips"));
    setCatalog(await api<OnboardCatalog>("/catalog"));
    if (trip) setTrip(await api<OnboardTrip>(`/trips/${trip.id}`));
  }, [api, trip?.id]);
  useEffect(() => {
    void load().catch((e) => setError(String(e)));
  }, [load]);
  async function action(path: string, body: unknown) {
    setBusy(true);
    try {
      await api(path, body);
      await load();
      setError("");
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }
  const field = (form: FormData, name: string) => String(form.get(name) ?? "");
  function repair(d: OnboardDefect) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          void action("/repairs", {
            defectId: d.id,
            assignedMembershipId: field(f, "member"),
            comment: field(f, "comment"),
            workTypeId: field(f, "workType") || null,
          });
        }}
      >
        <label>
          Исполнитель
          <select required name="member">
            {catalog?.memberships.map((m) => (
              <option key={m.id} value={m.id}>
                {m.user.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Вид работ
          <select name="workType">
            <option value="">Не задан</option>
            {catalog?.workTypes.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Ремонтное задание
          <input name="comment" />
        </label>
        <button disabled={busy}>Создать ремонтное задание</button>
      </form>
    );
  }
  function task(r: OnboardRepair) {
    return (
      <div key={r.id}>
        <p>
          Ремонт: {onboardLabel(r.status ?? "")} · Решение о наряде:{" "}
          {onboardLabel(r.permitDecision ?? "")} · Наряд:{" "}
          {r.workOrderId ? "связан" : "не связан"}
          {r.workOrderId &&
            catalog?.workOrders.find((w) => w.id === r.workOrderId) && (
              <button
                onClick={() =>
                  onOpenWorkOrder?.(
                    catalog.workOrders.find((w) => w.id === r.workOrderId)!
                      .number,
                  )
                }
              >
                Открыть наряд №
                {catalog.workOrders.find((w) => w.id === r.workOrderId)!.number}
              </button>
            )}
        </p>
        {["planned", "assigned"].includes(r.status) && (
          <>
            <button
              disabled={busy}
              onClick={() =>
                void action(`/repairs/${r.id}/status`, {
                  status: r.status,
                  permitDecision: "without_work_order",
                })
              }
            >
              Руководитель: наряд не требуется
            </button>
            <button
              disabled={busy}
              onClick={() =>
                void action(`/repairs/${r.id}/status`, {
                  status: r.status,
                  permitDecision: "work_order_required",
                })
              }
            >
              Руководитель: требуется наряд
            </button>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void action(`/repairs/${r.id}/status`, {
                  status: r.status,
                  workOrderId: field(
                    new FormData(e.currentTarget),
                    "workOrder",
                  ),
                });
              }}
            >
              <label>
                Существующий наряд
                <select required name="workOrder">
                  <option value="">Выберите наряд</option>
                  {catalog?.workOrders.map((w) => (
                    <option key={w.id} value={w.id}>
                      №{w.number} · {onboardLabel(w.status ?? "")}
                    </option>
                  ))}
                </select>
              </label>
              <button disabled={busy}>Связать</button>
            </form>
          </>
        )}
        {r.status === "assigned" &&
          r.assignedMembershipId === organizationMembershipId && (
            <button
              disabled={busy}
              onClick={() =>
                void action(`/repairs/${r.id}/status`, {
                  status: "in_progress",
                })
              }
            >
              Начать ремонт
            </button>
          )}
        {r.status === "in_progress" &&
          r.assignedMembershipId === organizationMembershipId && (
            <button
              disabled={busy}
              onClick={() =>
                void action(`/repairs/${r.id}/status`, { status: "completed" })
              }
            >
              Ремонт выполнен
            </button>
          )}
      </div>
    );
  }
  return (
    <section className="onboard-panel">
      <h2>БЭМ · сопровождение поезда</h2>
      {error && <p role="alert">{error}</p>}
      <button onClick={() => void load().catch((e) => setError(String(e)))}>
        Обновить
      </button>
      <table>
        <thead>
          <tr>
            <th>Рейс</th>
            <th>БЭМ</th>
            <th>Статус</th>
            <th>Вагоны</th>
            <th>План / выполнено / просрочено</th>
            <th>Открытые / критические дефекты</th>
          </tr>
        </thead>
        <tbody>
          {trips.map((t) => {
            const latest =
              [...t.inspections]
                .reverse()
                .find((i) => i.status === "in_progress") ??
              [...t.inspections]
                .reverse()
                .find((i) => i.status === "completed") ??
              t.inspections.at(-1);
            return (
              <tr key={t.id}>
                <td>
                  <button
                    onClick={() =>
                      void api<OnboardTrip>(`/trips/${t.id}`)
                        .then(setTrip)
                        .catch((e) => setError(String(e)))
                    }
                  >
                    №{t.trainNumber} {t.stationSnapshot.departure} →{" "}
                    {t.stationSnapshot.arrival}
                  </button>
                </td>
                <td>{t.assignee?.user.name}</td>
                <td>{onboardLabel(t.status ?? "")}</td>
                <td>
                  {latest?.results.length ?? 0}/{t.compositionSnapshot.length}
                </td>
                <td>
                  {t.inspections.length} /{" "}
                  {t.inspections.filter((i) => i.status === "completed").length}{" "}
                  /{" "}
                  {
                    t.inspections.filter(
                      (i) =>
                        i.status === "missed" ||
                        (i.status === "planned" &&
                          Date.parse(i.plannedAt) < Date.now()),
                    ).length
                  }
                </td>
                <td>
                  {t.defects.filter((d) => d.status !== "closed").length} /{" "}
                  {
                    t.defects.filter(
                      (d) => d.status !== "closed" && d.priority === "critical",
                    ).length
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {trip && (
        <article>
          <h3>Рейс №{trip.trainNumber}</h3>
          <button onClick={() => setTrip(null)}>Закрыть карточку</button>
          <p>
            {trip.stationSnapshot.departure} → {trip.stationSnapshot.arrival} ·{" "}
            {onboardLabel(trip.status ?? "")}
          </p>
          <h4>Состав</h4>
          {trip.compositionSnapshot.map((c) => (
            <p key={c.id}>
              Вагон {c.carriageNumber} · {c.type} · QR: {c.qrValue}
            </p>
          ))}
          {trip.status === "planned" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void action(`/trips/${trip.id}/status`, {
                  status: "planned",
                  assignedMembershipId: field(
                    new FormData(e.currentTarget),
                    "assigned",
                  ),
                });
              }}
            >
              <label>
                Назначить БЭМ
                <select name="assigned">
                  {catalog?.memberships.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.user.name}
                    </option>
                  ))}
                </select>
              </label>
              <button disabled={busy}>Назначить</button>
            </form>
          )}
          <h4>Обходы</h4>
          {trip.inspections.map((i) => (
            <details key={i.id}>
              <summary>
                №{i.inspectionNumber} · {onboardLabel(i.type ?? "")} ·{" "}
                {new Date(i.plannedAt).toLocaleString()} ·{" "}
                {onboardLabel(i.status ?? "")} · {i.results.length}/
                {trip.compositionSnapshot.length}
              </summary>
              <p>
                Checklist {i.templateSnapshot.name} v
                {i.templateSnapshot.version}
              </p>
              {i.results.map((r) => (
                <div key={r.id}>
                  <strong>
                    Вагон{" "}
                    {
                      trip.compositionSnapshot.find(
                        (c) => c.id === r.carriageId,
                      )?.carriageNumber
                    }
                  </strong>
                  {r.answers.map((a) => (
                    <p key={a.itemId}>
                      {
                        i.templateSnapshot.items.find((x) => x.id === a.itemId)
                          ?.label
                      }
                      : {onboardLabel(a.status ?? "")} {a.comment}
                      {a.photo && (
                        <img
                          alt="Фото замечания"
                          width={160}
                          src={`data:${a.photo.mimeType};base64,${a.photo.contentBase64}`}
                        />
                      )}
                    </p>
                  ))}
                </div>
              ))}
            </details>
          ))}
          {["planned", "active"].includes(trip.status) && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void action(`/trips/${trip.id}/inspections`, {
                  templateId: field(f, "template"),
                  type: field(f, "type"),
                  plannedAt: new Date(field(f, "plannedAt")).toISOString(),
                });
              }}
            >
              <label>
                Шаблон
                <select name="template">
                  {catalog?.templates.map((t) => (
                    <option value={t.id} key={t.id}>
                      {t.name} v{t.version}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Тип
                <select name="type">
                  <option value="periodic">Обход</option>
                  <option value="acceptance">Приёмка</option>
                </select>
              </label>
              <label>
                План
                <input required name="plannedAt" type="datetime-local" />
              </label>
              <button disabled={busy}>Запланировать вручную</button>
            </form>
          )}
          <h4>Дефекты и ремонт</h4>
          {trip.defects.map((d) => (
            <div key={d.id}>
              <p>
                {d.description} · {onboardLabel(d.priority ?? "")} ·{" "}
                {onboardLabel(d.status ?? "")}
              </p>
              <p>
                Эксплуатация вагона: {onboardLabel(d.operationAllowed ?? "")}
              </p>
              {(["yes", "no", "requires_decision"] as const).map(
                (operationAllowed) => (
                  <button
                    key={operationAllowed}
                    disabled={busy}
                    onClick={() =>
                      void action(`/defects/${d.id}/status`, {
                        status: d.status,
                        operationAllowed,
                      })
                    }
                  >
                    Эксплуатация: {operationAllowed}
                  </button>
                ),
              )}
              {["open", "assigned"].includes(d.status) && repair(d)}
              {d.repairTasks.map(task)}
              {["resolved", "verified"].includes(d.status) && (
                <button
                  disabled={busy}
                  onClick={() =>
                    void action(`/defects/${d.id}/status`, {
                      status: d.status === "resolved" ? "verified" : "closed",
                    })
                  }
                >
                  {d.status === "resolved"
                    ? "Устранение проверено"
                    : "Закрыть дефект"}
                </button>
              )}
            </div>
          ))}
          <h4>Timeline / итоги</h4>
          <p>
            Обходов завершено:{" "}
            {trip.inspections.filter((i) => i.status === "completed").length};
            дефектов закрыто:{" "}
            {trip.defects.filter((d) => d.status === "closed").length}
          </p>
          {trip.timeline?.map((e) => (
            <p key={e.id}>
              {new Date(e.occurredAt).toLocaleString()} ·{" "}
              {onboardLabel(e.details.action ?? "")}{" "}
              {onboardLabel(e.details.status ?? "")}
            </p>
          ))}
        </article>
      )}
      <details>
        <summary>Создать рейс</summary>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void action("/trips", {
              trainId: field(f, "train"),
              trainNumber: field(f, "number"),
              departureStationId: field(f, "departure"),
              arrivalStationId: field(f, "arrival"),
              assignedMembershipId: field(f, "member"),
              plannedStart: new Date(field(f, "start")).toISOString(),
              plannedEnd: new Date(field(f, "end")).toISOString(),
              intervalMinutes: field(f, "interval")
                ? Number(field(f, "interval"))
                : null,
            });
          }}
        >
          <label>
            Поезд
            <select name="train">
              {catalog?.trains.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.number} {t.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Номер
            <input name="number" required />
          </label>
          {["departure", "arrival"].map((name) => (
            <label key={name}>
              {name === "departure" ? "Отправление" : "Прибытие"}
              <select name={name}>
                {catalog?.stations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <label>
            БЭМ
            <select name="member">
              {catalog?.memberships.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.user.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Начало
            <input required type="datetime-local" name="start" />
          </label>
          <label>
            Конец
            <input required type="datetime-local" name="end" />
          </label>
          <label>
            Интервал, минут (пусто — ручной план)
            <input type="number" min={1} name="interval" />
          </label>
          <button disabled={busy}>Создать</button>
        </form>
      </details>
      <details>
        <summary>Поезд и вагоны</summary>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const numbers = field(f, "cars")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean);
            void action("/trains", {
              id: field(f, "trainId") || undefined,
              number: field(f, "trainNumber"),
              name: field(f, "trainName"),
              carriages: numbers.map((carriageNumber) => ({
                carriageNumber,
                type: field(f, "carType"),
                qrValue: `${field(f, "qrPrefix")}:${carriageNumber}`,
              })),
            });
          }}
        >
          <label>
            Поезд
            <select name="trainId">
              <option value="">Новый</option>
              {catalog?.trains.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.number} {t.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Номер
            <input required name="trainNumber" />
          </label>
          <label>
            Название
            <input required name="trainName" />
          </label>
          <label>
            Номера вагонов через запятую
            <input required name="cars" placeholder="01, 02, 03" />
          </label>
          <label>
            Тип вагонов
            <input required name="carType" />
          </label>
          <label>
            Префикс идентификаторов QR
            <input required name="qrPrefix" />
          </label>
          <button disabled={busy}>Сохранить состав</button>
          <p>Изменение справочника не меняет снимки существующих рейсов.</p>
        </form>
      </details>
      <details>
        <summary>Редактировать checklist</summary>
        <select
          value={templateId}
          onChange={(e) => {
            setTemplateId(e.target.value);
            const t = catalog?.templates.find((x) => x.id === e.target.value);
            setItems(t?.items ?? []);
            setTemplateName(t?.name ?? "Checklist БЭМ");
          }}
        >
          <option value="">Новый шаблон</option>
          {catalog?.templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} v{t.version}
            </option>
          ))}
        </select>
        <input
          aria-label="Название шаблона"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        {items.map((item, index) => (
          <div key={item.id}>
            <input
              aria-label="Категория"
              value={item.category}
              onChange={(e) =>
                setItems(
                  items.map((x, i) =>
                    i === index ? { ...x, category: e.target.value } : x,
                  ),
                )
              }
            />
            <input
              aria-label="Пункт"
              value={item.label}
              onChange={(e) =>
                setItems(
                  items.map((x, i) =>
                    i === index ? { ...x, label: e.target.value } : x,
                  ),
                )
              }
            />
            <button
              onClick={() => setItems(items.filter((_, i) => i !== index))}
            >
              Удалить пункт
            </button>
          </div>
        ))}
        <button
          onClick={() =>
            setItems([
              ...items,
              { id: crypto.randomUUID(), category: "", label: "" },
            ])
          }
        >
          Добавить пункт
        </button>
        <button
          disabled={busy}
          onClick={() =>
            void action("/templates", {
              id: templateId || undefined,
              name: templateName,
              items,
            })
          }
        >
          Сохранить новую версию
        </button>
        <p>Обходы сохраняют прежние версии checklist.</p>
      </details>
    </section>
  );
}
